import {
    HorizontalPadding,
    Ratio,
    SystemPage,
    TopPadding,
} from '@easeltv/client-sdk';
import { STRING_PLACEHOLDER_REGEX } from '@easeltv/utilities';
import { ReactNode } from 'react';

/* -------------------------------- Functions ------------------------------- */

/** @function `getRatioFactors()`
 * Returns an object containing numerical values representing factors for width or height,
 * e.g. { widthFactor: 21, heightFactor: 9 } from `21:9`
 *
 * @param ratio  Ratio enum e.g. "21:9" or "4:3"
 * @returns Object, destructured to width and height properties e.g. { widthFactor: 4, heightFactor: 3 }
 */
export const getRatioFactors = (ratio: Ratio) => ({
    widthFactor: parseInt(ratio.split(':')[0], 10),
    heightFactor: parseInt(ratio.split(':')[1], 10),
});

/** @function `getRatioFraction()`
 * Returns the fractional value of a ratio,
 * e.g. 2.333 from `21:9`, or 1 from `1:1`
 *
 * @param ratio  Ratio enum e.g. "21:9" or "4:3"
 * @returns the fractional value, e.g. 1.333 from "4:3"
 */
export const getRatioFraction = (ratio: Ratio) => {
    const { widthFactor, heightFactor } = getRatioFactors(ratio);
    return widthFactor / heightFactor;
};

/** @function `getPaddingFromRatio()` *
 * Returns amount of padding needed to maintain aspect ratio
 *
 * @param ratio  Ratio enum e.g. "21:9" or "4:3"
 * @returns String e.g. "56.25%", "42.85%", "75%"
 */
export const getPaddingFromRatio = (ratio: Ratio) => {
    const { widthFactor, heightFactor } = getRatioFactors(ratio);
    return `${((heightFactor / widthFactor) * 100).toFixed(2)}%`;
};

/**
 * replaceWithLink
 *
 * Injects the given links into one or more placeholders
 *
 * @TODO: Unit testing
 * Possible test cases:
 * Single replace - `{0}` => `<a>Link 0</a>`
 * Multiple replace - `{0} - {1} - {2}` => `<a>Link 0</a> - <a>Link 1</a> - <a>Link 2</a>`
 *
 * Potential Improvements:
 * Repeated replace - `{0} - {1} - {0}` => `<a>Link 0</a> - <a>Link 1</a> - <a>Link 0</a>`
 *
 * @param text String from the dictionary with placeholders
 * @param links An array of links to be injected into the placeholder spaces
 * @returns An array of nodes to be rendered
 */
export function replaceWithLink(
    text: string,
    links: { url: string; label: string }[],
) {
    return (
        text
            // Split using the placeholder regex
            .split(STRING_PLACEHOLDER_REGEX)
            // Reduce the resultant array, adding in the links between chunks
            .reduce<Array<ReactNode>>((sum, chunk, index) => {
                // Find link from links array
                const link = links[index];

                // Return sum, plus this chunk and the link if it exists
                return [
                    ...sum,
                    chunk,
                    link ? (
                        <a
                            key={link.url}
                            href={link.url}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {link.label}
                        </a>
                    ) : undefined,
                ];
            }, [])
            // Filter out any nodes that are undefined
            .filter(Boolean)
    );
}

/**
 * dictionaryStringWithArgument()
 *
 * Replaces dictionary strings with argument placeholders with the provided argument.
 *
 * @param text - The dictionary string with an argument placeholder
 * @param argument - The argument to substitute with the placeholder
 * @returns string - The dictionary string with the replaced arguments
 */
export function dictionaryStringWithArgument(
    text: string,
    argument: string,
) {
    return text.replace('{0}', argument);
}

/**
 * removeEmptyStringFromObject
 *
 * Returns a copy of a given object with all empty string keys removed
 *
 * @param obj
 * @returns
 */
export function removeEmptyStringFromObject<T extends object>(obj: T) {
    return (
        Object.entries(obj)
            // Filter out entries with empty string values
            .filter(([, value]) => value !== '')
            // Reduce the resultant array of [key, value] pairs back into an object
            .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}) as T
    );
}

/**
 * containsOnlyLetters()
 *
 * Tests that all characters are lowercase and uppercase ASCII letters.
 * Returns false for an empty string.
 *
 * @param text - Input string to test
 * @returns boolean - True if all characters are lowercase and uppercase ASCII letters
 */
export const containsOnlyLetters = (text: string) => /^[a-z]+$/i.test(text);

/**
 * interpretFooterSpacing()
 *
 * Returns CSS values
 *
 * @param spacing - TopPadding or HorizontalPadding types
 * @returns string - Value in REM e.g '1.25rem'
 */
export const interpretFooterSpacing = (
    spacing: TopPadding | HorizontalPadding,
): string => {
    switch (spacing) {
        case TopPadding.Large:
        case HorizontalPadding.Large: {
            return '3.75rem'; /** 60px */
        }
        case TopPadding.Medium:
        case HorizontalPadding.Medium: {
            return '2.5rem'; /** 40px */
        }
        case TopPadding.Small:
        case HorizontalPadding.Small: {
            return '1.25rem'; /** 20px */
        }
        default: {
            return '0';
        }
    }
};

/**
 * getSystemPath()
 *
 * Returns system path for the provided systemPage name.
 * Returns an empty string if no path is matched
 *
 * @param systemPage - SystemPage, 'registration' or ' login'
 * @returns string - Path to the appropriate page e.g '/signin'
 */
export const getSystemPath = (systemPage: SystemPage): string => {
    switch (systemPage) {
        case SystemPage.Login: {
            return '/signin';
        }
        case SystemPage.Logout: {
            return '/register';
        }
        case SystemPage.AccountDetails: {
            return '/account';
        }
        case SystemPage.Payments: {
            return '/billing-history';
        }
        case SystemPage.PrivacySettings: {
            return '/privacy';
        }
        case SystemPage.Registration: {
            return '/register';
        }
        case SystemPage.Subscribe: {
            return '/subscribe';
        }
        case SystemPage.Subscriptions: {
            return '/subscriptions';
        }
        case SystemPage.UpdateCard: {
            return '/payment-details';
        }
        default: {
            return '';
        }
    }
};
